import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { fixPhotoUrl } from '@/lib/photoUrl';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  ArrowRight,
  Edit,
  EyeOff,
  Upload,
  X,
  Image,
  Calendar,
  Trash2,
  Key,
  Star,
  GripVertical,
  LocateFixed,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Parse a coordinate string entered by the admin.
 *
 * Handles two real-world failure modes we've hit in production:
 *   1. European decimal commas: "-0,766" should become -0.766.
 *      (HTML number inputs are locale-sensitive about commas; in some
 *      browsers the comma is silently stripped, which produced a
 *      17-digit longitude that OOMed the frontend Leaflet map.)
 *   2. Stray whitespace from copy-paste.
 *
 * Returns:
 *   - null if the input is empty
 *   - a finite number if valid AND within [min,max]
 *   - NaN if the input was provided but couldn't be parsed or is out of range
 *
 * Caller decides what to do with NaN (typically: show an inline error).
 */
function parseCoordinate(raw, min, max) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const cleaned = String(raw).trim().replace(',', '.');
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return NaN;
  if (n < min || n > max) return NaN;
  return n;
}

const PropertiesPage = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    reference: '',
    key_reference: '',
    exact_address: '',
    wifi_ssid: '',
    wifi_password: '',
    property_type: '',
    description: '',
    amenities: [],
    // Per-language overrides for title / description / amenities. Shape:
    //   { es: { title?, description?, amenities?: string[] }, fi: {...}, sv: {...}, de: {...} }
    // Empty sub-objects are fine — the backend strips empty fields on save
    // and missing fields fall back to English on the public site.
    translations: { es: {}, fi: {}, sv: {}, de: {} },
    location: { address_line_1: '', city: '', country: '' },
    bedrooms: 1,
    bathrooms: 1,
    max_guests: 4,
    check_in_fee: 0,
    check_out_fee: 0,
    cleaning_fee: 0,
    min_stay_nights: 11,
    allow_7_night_min: false,
    base_nightly_rate: 150,
    owner_id: '',
    operational_notes: '',
    is_active: true,
    is_published: true,
    photos: [],
    seasonal_rates: [],
    length_of_stay_rates: []
  });

  // Active sub-tab inside the "Translations" main tab ('es'|'fi'|'sv'|'de').
  const [translationLang, setTranslationLang] = useState('es');
  const [autoTranslating,  setAutoTranslating]  = useState(false);
  
  const [newAmenity, setNewAmenity] = useState('');
  const [customAmenities, setCustomAmenities] = useState([]);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const dragIndex = useRef(null);
  const [geocoding, setGeocoding] = useState(false);

  // Default seasonal rate blocks
  const defaultSeasonalBlocks = [
    { name: 'Low Season', start_month: 1, end_month: 3, daily_rate: 0, discount_percentage: 0 },
    { name: 'Mid Season', start_month: 4, end_month: 6, daily_rate: 0, discount_percentage: 0 },
    { name: 'High Season', start_month: 7, end_month: 9, daily_rate: 0, discount_percentage: 0 },
    { name: 'Peak Season', start_month: 10, end_month: 12, daily_rate: 0, discount_percentage: 0 },
  ];

  // Default length-of-stay rate brackets
  const defaultLengthOfStayBrackets = [
    { min_nights: 7, max_nights: 10, rate_type: 'multiplier', multiplier: 1.0, fixed_rate: 0 },
    { min_nights: 11, max_nights: 13, rate_type: 'multiplier', multiplier: 0.95, fixed_rate: 0 },
    { min_nights: 14, max_nights: 21, rate_type: 'multiplier', multiplier: 0.90, fixed_rate: 0 },
    { min_nights: 22, max_nights: 30, rate_type: 'multiplier', multiplier: 0.85, fixed_rate: 0 },
    { min_nights: 31, max_nights: 999, rate_type: 'multiplier', multiplier: 0.80, fixed_rate: 0 },
  ];

  const isAdmin = user?.role === 'ADMIN';
  const isCustomerService = user?.role === 'CUSTOMER_SERVICE';
  const isOwner = user?.role === 'OWNER';
  const canSeeKeyReference = isAdmin || isCustomerService;

  useEffect(() => {
    fetchProperties();
    if (isAdmin) {
      fetchOwners();
    }
  }, [search]);

  const fetchProperties = async () => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/properties`, { params });
      setProperties(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API}/owners`);
      setOwners(response.data.items || []);
    } catch (error) {
      console.error('Failed to load owners');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean the translations object: strip empty sub-objects and trim
      // blank amenity lines so MongoDB stays tidy.
      const cleanTranslations = {};
      ['es', 'fi', 'sv', 'de'].forEach((code) => {
        const tr = (formData.translations || {})[code] || {};
        const sub = {};
        if (tr.title && tr.title.trim())               sub.title       = tr.title.trim();
        if (tr.description && tr.description.trim())   sub.description = tr.description;
        if (Array.isArray(tr.amenities)) {
          const cleaned = tr.amenities.map((x) => (x || '').trim()).filter(Boolean);
          if (cleaned.length) sub.amenities = cleaned;
        }
        if (Object.keys(sub).length) cleanTranslations[code] = sub;
      });

      const submitData = {
        ...formData,
        translations: cleanTranslations,
        location: {
          address_line_1: formData.location.address_line_1 || formData.location.address || '',
          city: formData.location.city || '',
          country: formData.location.country || '',
          latitude: formData.location.latitude ?? null,
          longitude: formData.location.longitude ?? null
        },
        photos: formData.photos.map((p, i) => ({ url: p.url, is_primary: p.is_primary, sort_order: i }))
      };

      if (selectedProperty) {
        await axios.put(`${API}/properties/${selectedProperty.id}`, submitData);
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, submitData);
        toast.success('Property created successfully');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => e.msg || JSON.stringify(e)).join('; ')
        : (detail || 'Operation failed');
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      reference: '',
      key_reference: '',
      exact_address: '',
      wifi_ssid: '',
      wifi_password: '',
      property_type: '',
      description: '',
      amenities: [],
      translations: { es: {}, fi: {}, sv: {}, de: {} },
      location: { address_line_1: '', city: '', country: '' },
      check_in_fee: 0,
      check_out_fee: 0,
      cleaning_fee: 0,
      min_stay_nights: 11,
      allow_7_night_min: false,
      base_nightly_rate: 150,
      owner_id: '',
      operational_notes: '',
      is_active: true,
      is_published: true,
      photos: [],
      seasonal_rates: [],
      length_of_stay_rates: []
    });
    setSelectedProperty(null);
    setActiveTab('basic');
    setTranslationLang('es');
    setNewAmenity('');
  };

  const openEditDialog = (property) => {
    setSelectedProperty(property);
    // Merge translations from the backend into the default scaffold so
    // every language tab always has a sub-object to render.
    const mergedTranslations = {
      es: {}, fi: {}, sv: {}, de: {},
      ...(property.translations || {}),
    };
    setFormData({
      ...property,
      reference: property.reference || '',
      key_reference: property.key_reference || '',
      exact_address: property.exact_address || '',
      wifi_ssid: property.wifi_ssid || '',
      wifi_password: property.wifi_password || '',
      property_type: property.property_type || '',
      bedrooms: property.bedrooms || 1,
      bathrooms: property.bathrooms || 1,
      max_guests: property.max_guests || 4,
      amenities: property.amenities || [],
      translations: mergedTranslations,
      location: property.location || { address_line_1: '', city: '', country: '' },
      photos: (property.photos || []).map((url, i) => ({ url, is_primary: i === 0 })),
      seasonal_rates: property.seasonal_rates || defaultSeasonalBlocks,
      length_of_stay_rates: property.length_of_stay_rates || defaultLengthOfStayBrackets
    });
    // Load any custom amenities from this property
    const existingAmenities = property.amenities || [];
    const customOnes = existingAmenities.filter(a => !defaultAmenitiesList.includes(a));
    setCustomAmenities(prev => [...new Set([...prev, ...customOnes])]);
    setTranslationLang('es');
    setShowCreateDialog(true);
  };

  // ── Translation helpers ────────────────────────────────────────────────
  const setTranslationField = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...(prev.translations || {}),
        [lang]: {
          ...((prev.translations || {})[lang] || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleAutoTranslate = async () => {
    if (!selectedProperty) {
      toast.error('Save the property first, then run auto-translate.');
      return;
    }
    setAutoTranslating(true);
    try {
      const res = await axios.post(
        `${API}/properties/${selectedProperty.id}/translate`,
        { overwrite: true }
      );
      // Merge the returned translations into the form so the tabs refresh immediately.
      const mergedTranslations = {
        es: {}, fi: {}, sv: {}, de: {},
        ...(res.data?.translations || {}),
      };
      setFormData((prev) => ({ ...prev, translations: mergedTranslations }));
      toast.success(`Auto-translated into ${(res.data?.translated || []).join(', ').toUpperCase()}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 503) {
        toast.error(detail || 'DeepL is not configured on this server.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Auto-translate failed');
      }
    } finally {
      setAutoTranslating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Upload each file to Vercel Blob via the backend (sequential keeps the
    // progress predictable and avoids overwhelming Blob with parallel PUTs).
    // The endpoint accepts an optional ?property_id= to namespace the Blob
    // path; for brand-new properties we omit it and files land under
    // /properties/_unassigned/.
    const propertyId = selectedProperty?.id;
    const qs = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : '';

    let uploaded = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/properties/photo${qs}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = res.data?.url;
        if (!url) throw new Error('No URL returned from upload');
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, { url, is_primary: prev.photos.length === 0 }],
        }));
        uploaded += 1;
      } catch (err) {
        failed += 1;
        const detail = err.response?.data?.detail || err.message;
        toast.error(`Upload failed for ${file.name}: ${detail}`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} photo(s) uploaded`);
    if (failed > 0) toast.error(`${failed} photo(s) failed`);
  };

  const removePhoto = (index) => {
    const newPhotos = formData.photos.filter((_, i) => i !== index);
    // If removed photo was primary and there are remaining photos, make first primary
    if (formData.photos[index]?.is_primary && newPhotos.length > 0) {
      newPhotos[0] = { ...newPhotos[0], is_primary: true };
    }
    setFormData({ ...formData, photos: newPhotos });
  };

  const setPrimaryPhoto = (index) => {
    const newPhotos = formData.photos.map((p, i) => ({ ...p, is_primary: i === index }));
    setFormData({ ...formData, photos: newPhotos });
  };

  const onPhotoDragStart = (e, index) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const onPhotoDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onPhotoDrop = (e, dropIndex) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === dropIndex) return;
    const newPhotos = [...formData.photos];
    const [dragged] = newPhotos.splice(from, 1);
    newPhotos.splice(dropIndex, 0, dragged);
    dragIndex.current = null;
    setFormData({ ...formData, photos: newPhotos });
  };

  const handleDeleteProperty = async () => {
    if (!propertyToDelete) return;
    try {
      await axios.delete(`${API}/properties/${propertyToDelete.id}`);
      toast.success(`"${propertyToDelete.title}" deleted`);
      setPropertyToDelete(null);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleGeocode = async () => {
    const addr = [
      formData.location.address_line_1,
      formData.location.city,
      formData.location.country
    ].filter(Boolean).join(', ');
    if (!addr) { toast.error('Enter an address first'); return; }
    setGeocoding(true);
    try {
      const res = await axios.get(`${API}/geocode`, { params: { q: addr } });
      setFormData(prev => ({
        ...prev,
        location: { ...prev.location, latitude: res.data.latitude, longitude: res.data.longitude }
      }));
      toast.success(`Located: ${res.data.display_name.split(',').slice(0, 2).join(', ')}`);
    } catch {
      toast.error('Could not locate address — try a more specific address');
    } finally {
      setGeocoding(false);
    }
  };

  const addSeasonalRate = () => {
    setFormData({
      ...formData,
      seasonal_rates: [
        ...formData.seasonal_rates,
        { name: 'New Season', start_month: 1, end_month: 3, daily_rate: 0, discount_percentage: 0 }
      ]
    });
  };

  const updateSeasonalRate = (index, field, value) => {
    const newRates = [...formData.seasonal_rates];
    newRates[index] = { ...newRates[index], [field]: value };
    setFormData({ ...formData, seasonal_rates: newRates });
  };

  const removeSeasonalRate = (index) => {
    const newRates = formData.seasonal_rates.filter((_, i) => i !== index);
    setFormData({ ...formData, seasonal_rates: newRates });
  };

  // Length-of-stay rate helpers
  const addLengthOfStayRate = () => {
    setFormData({
      ...formData,
      length_of_stay_rates: [
        ...formData.length_of_stay_rates,
        { min_nights: 7, max_nights: 10, rate_type: 'multiplier', multiplier: 1.0, fixed_rate: 0, monthly_rate: 0 }
      ]
    });
  };

  const updateLengthOfStayRate = (index, field, value) => {
    const newRates = [...formData.length_of_stay_rates];
    newRates[index] = { ...newRates[index], [field]: value };
    setFormData({ ...formData, length_of_stay_rates: newRates });
  };

  const removeLengthOfStayRate = (index) => {
    const newRates = formData.length_of_stay_rates.filter((_, i) => i !== index);
    setFormData({ ...formData, length_of_stay_rates: newRates });
  };

  // Custom amenity helper
  const addCustomAmenity = () => {
    if (newAmenity.trim() && !defaultAmenitiesList.includes(newAmenity.trim()) && !customAmenities.includes(newAmenity.trim())) {
      setCustomAmenities([...customAmenities, newAmenity.trim()]);
      setFormData({
        ...formData,
        amenities: [...formData.amenities, newAmenity.trim()]
      });
      setNewAmenity('');
      toast.success(`Added "${newAmenity.trim()}" to amenities`);
    } else if (defaultAmenitiesList.includes(newAmenity.trim()) || customAmenities.includes(newAmenity.trim())) {
      toast.error('This amenity already exists');
    }
  };

  const defaultAmenitiesList = [
    'WiFi', 'Pool', 'Beach Access', 'Air Conditioning', 'Kitchen', 
    'Parking', 'Gym', 'Spa', 'Hot Tub', 'Fireplace', 'Mountain View',
    'Ocean View', 'Pet Friendly', 'Laundry', 'TV', 'Balcony',
    'BBQ', 'Garden', 'Terrace', 'Heating', 'Workspace', 'EV Charger'
  ];

  // All amenities (default + custom)
  const allAmenities = [...defaultAmenitiesList, ...customAmenities];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">
            {isOwner ? 'My Properties' : 'Properties'}
          </h1>
          <p className="text-[#194a50]/70 mt-1">{properties.length} properties found</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#194a50]/50" />
            <Input
              placeholder="Search properties..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-64 border-[#194a50]/20"
              data-testid="search-input"
            />
          </div>
          {(isAdmin || isCustomerService) && (
            <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#194a50] hover:bg-[#10555e] rounded-full" data-testid="add-property-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-xl font-['Outfit']">
                    {selectedProperty ? 'Edit Property' : 'Add New Property'}
                  </DialogTitle>
                </DialogHeader>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="photos">Photos</TabsTrigger>
                    <TabsTrigger value="amenities">Amenities</TabsTrigger>
                    <TabsTrigger value="pricing">Pricing</TabsTrigger>
                    <TabsTrigger value="translations">🌍 Translations</TabsTrigger>
                  </TabsList>
                  
                  <form onSubmit={handleSubmit}>
                    {/* Basic Info Tab */}
                    <TabsContent value="basic" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Title</Label>
                          <Input
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                            data-testid="property-title-input"
                          />
                        </div>
                        <div>
                          <Label>Property Reference</Label>
                          <Input
                            value={formData.reference}
                            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                            placeholder="e.g., PROP-001"
                            data-testid="property-reference-input"
                          />
                        </div>
                        <div>
                          <Label>Property Type</Label>
                          <Select
                            value={formData.property_type || ''}
                            onValueChange={(val) => setFormData({ ...formData, property_type: val })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="apartment">Apartment</SelectItem>
                              <SelectItem value="villa">Villa</SelectItem>
                              <SelectItem value="townhouse">Townhouse</SelectItem>
                              <SelectItem value="penthouse">Penthouse</SelectItem>
                              <SelectItem value="bungalow">Bungalow</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {canSeeKeyReference && (
                          <div>
                            <Label>Key Reference <span className="text-xs text-amber-600">(Internal)</span></Label>
                            <Input
                              value={formData.key_reference}
                              onChange={(e) => setFormData({ ...formData, key_reference: e.target.value })}
                              placeholder="e.g., Safe code, lockbox..."
                              data-testid="property-key-reference-input"
                            />
                          </div>
                        )}
                        <div className="col-span-2">
                          <Label>Description</Label>
                          <RichTextEditor
                            value={formData.description}
                            onChange={(html) => setFormData({ ...formData, description: html })}
                            placeholder="Write a property description… Use headings, bold, lists to structure the content."
                            minHeight={260}
                            testId="property-description-input"
                          />
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input
                            value={formData.location.address_line_1 || formData.location.address || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              location: { ...formData.location, address_line_1: e.target.value }
                            })}
                            data-testid="property-address-input"
                          />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input
                            value={formData.location.city}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              location: { ...formData.location, city: e.target.value }
                            })}
                            data-testid="property-city-input"
                          />
                        </div>
                        <div>
                          <Label>Country</Label>
                          <Input
                            value={formData.location.country}
                            onChange={(e) => setFormData({
                              ...formData,
                              location: { ...formData.location, country: e.target.value }
                            })}
                            data-testid="property-country-input"
                          />
                        </div>

                        {canSeeKeyReference && (
                          <>
                            <div className="col-span-2">
                              <Label>Exact Address <span className="text-xs text-amber-600">(Internal — not shown publicly)</span></Label>
                              <Input
                                value={formData.exact_address || ''}
                                onChange={(e) => setFormData({ ...formData, exact_address: e.target.value })}
                                placeholder="Full street address, door/apt number, urbanisation…"
                                data-testid="property-exact-address-input"
                              />
                            </div>
                            <div>
                              <Label>WiFi Network (SSID) <span className="text-xs text-amber-600">(Internal)</span></Label>
                              <Input
                                value={formData.wifi_ssid || ''}
                                onChange={(e) => setFormData({ ...formData, wifi_ssid: e.target.value })}
                                placeholder="Network name"
                                data-testid="property-wifi-ssid-input"
                              />
                            </div>
                            <div>
                              <Label>WiFi Password <span className="text-xs text-amber-600">(Internal)</span></Label>
                              <Input
                                value={formData.wifi_password || ''}
                                onChange={(e) => setFormData({ ...formData, wifi_password: e.target.value })}
                                placeholder="Password / router key"
                                data-testid="property-wifi-password-input"
                              />
                            </div>
                          </>
                        )}

                        {/* Coordinates row */}
                        <div className="col-span-2">
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label>Latitude</Label>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={formData.location.latitude ?? ''}
                                onChange={(e) => {
                                  const parsed = parseCoordinate(e.target.value, -90, 90);
                                  if (Number.isNaN(parsed)) {
                                    toast.error('Latitude must be a number between −90 and 90');
                                    return;
                                  }
                                  setFormData({
                                    ...formData,
                                    location: { ...formData.location, latitude: parsed }
                                  });
                                }}
                                placeholder="e.g. 37.9838"
                              />
                            </div>
                            <div className="flex-1">
                              <Label>Longitude</Label>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={formData.location.longitude ?? ''}
                                onChange={(e) => {
                                  const parsed = parseCoordinate(e.target.value, -180, 180);
                                  if (Number.isNaN(parsed)) {
                                    toast.error('Longitude must be a number between −180 and 180');
                                    return;
                                  }
                                  setFormData({
                                    ...formData,
                                    location: { ...formData.location, longitude: parsed }
                                  });
                                }}
                                placeholder="e.g. -0.1134"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleGeocode}
                              disabled={geocoding}
                              className="shrink-0 border-[#194a50]/30 text-[#194a50] hover:bg-[#194a50]/5"
                              title="Auto-detect coordinates from address"
                            >
                              {geocoding
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <LocateFixed className="w-4 h-4" />}
                              <span className="ml-1.5 text-xs">Locate</span>
                            </Button>
                          </div>
                          <p className="text-xs text-[#194a50]/50 mt-1">
                            Used to pin the property on the map. Click Locate to auto-fill from the address above.
                          </p>
                        </div>

                        <div>
                          <Label>Bedrooms</Label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.bedrooms}
                            onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 1 })}
                            data-testid="property-bedrooms-input"
                          />
                        </div>
                        <div>
                          <Label>Bathrooms</Label>
                          <Input
                            type="number"
                            min="1"
                            step="0.5"
                            value={formData.bathrooms}
                            onChange={(e) => setFormData({ ...formData, bathrooms: parseFloat(e.target.value) || 1 })}
                            data-testid="property-bathrooms-input"
                          />
                        </div>
                        <div>
                          <Label>Max Guests</Label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.max_guests}
                            onChange={(e) => setFormData({ ...formData, max_guests: parseInt(e.target.value) || 1 })}
                            data-testid="property-max-guests-input"
                          />
                        </div>
                        <div>
                          <Label>Owner</Label>
                          <Select 
                            value={formData.owner_id} 
                            onValueChange={(val) => setFormData({ ...formData, owner_id: val })}
                          >
                            <SelectTrigger data-testid="owner-select">
                              <SelectValue placeholder="Select owner" />
                            </SelectTrigger>
                            <SelectContent>
                              {owners.filter(o => o.owner_profile_id).map((owner) => (
                                <SelectItem key={owner.id} value={owner.owner_profile_id}>
                                  {owner.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label>Operational Notes (Internal)</Label>
                          <Textarea
                            value={formData.operational_notes}
                            onChange={(e) => setFormData({ ...formData, operational_notes: e.target.value })}
                            rows={2}
                            placeholder="Internal notes for staff..."
                            data-testid="notes-input"
                          />
                        </div>
                        <div className="col-span-2 flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.is_active}
                              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                              className="w-4 h-4 rounded border-[#194a50]/30"
                            />
                            <span className="text-sm text-[#194a50]">Active</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.is_published}
                              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                              className="w-4 h-4 rounded border-[#194a50]/30"
                            />
                            <span className="text-sm text-[#194a50]">Published</span>
                          </label>
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Photos Tab */}
                    <TabsContent value="photos" className="space-y-4 mt-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label>Property Photos</Label>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1.5 text-xs text-[#194a50] hover:text-[#0f3338] font-medium"
                          >
                            <Upload className="w-3.5 h-3.5" /> Add photos
                          </button>
                        </div>
                        <p className="text-xs text-[#194a50]/60 mb-3">
                          Drag to reorder · Click ★ to set as featured cover image
                        </p>

                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handlePhotoUpload}
                          accept="image/*"
                          multiple
                          className="hidden"
                        />

                        <div className="grid grid-cols-3 gap-3">
                          {formData.photos.map((photo, index) => (
                            <div
                              key={index}
                              draggable
                              onDragStart={(e) => onPhotoDragStart(e, index)}
                              onDragOver={onPhotoDragOver}
                              onDrop={(e) => onPhotoDrop(e, index)}
                              className="relative group aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing"
                            >
                              <img src={fixPhotoUrl(photo.url)} alt={`Property ${index + 1}`} className="w-full h-full object-cover" />

                              {/* Featured badge */}
                              {photo.is_primary && (
                                <div className="absolute top-2 left-2 bg-amber-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                  <Star className="w-2.5 h-2.5 fill-white" /> Featured
                                </div>
                              )}

                              {/* Drag handle hint */}
                              <div className="absolute bottom-2 left-2 text-white/60 opacity-0 group-hover:opacity-100 transition-opacity">
                                <GripVertical className="w-4 h-4" />
                              </div>

                              {/* Actions overlay */}
                              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {/* Star / set primary */}
                                <button
                                  type="button"
                                  onClick={() => setPrimaryPhoto(index)}
                                  title="Set as featured image"
                                  className={`p-1 rounded-full shadow transition-colors ${photo.is_primary ? 'bg-amber-400 text-white' : 'bg-white/90 text-gray-500 hover:bg-amber-400 hover:text-white'}`}
                                >
                                  <Star className={`w-3.5 h-3.5 ${photo.is_primary ? 'fill-white' : ''}`} />
                                </button>
                                {/* Remove */}
                                <button
                                  type="button"
                                  onClick={() => removePhoto(index)}
                                  title="Remove photo"
                                  className="p-1 bg-red-500 text-white rounded-full shadow hover:bg-red-600 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              {/* Order badge */}
                              <div className="absolute bottom-2 right-2 bg-black/40 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                {index + 1}
                              </div>
                            </div>
                          ))}

                          {/* Add photo tile */}
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="aspect-video border-2 border-dashed border-[#194a50]/30 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#194a50] hover:bg-[#194a50]/5 transition-colors"
                          >
                            <Upload className="w-6 h-6 text-[#194a50]/50" />
                            <span className="text-xs text-[#194a50]/60">Add Photo</span>
                          </button>
                        </div>

                        {formData.photos.length > 0 && (
                          <p className="text-xs text-[#194a50]/50 mt-2">
                            {formData.photos.length} photo{formData.photos.length > 1 ? 's' : ''} · First photo shown as default if no featured set
                          </p>
                        )}
                      </div>
                    </TabsContent>
                    
                    {/* Amenities Tab */}
                    <TabsContent value="amenities" className="space-y-4 mt-4">
                      <div>
                        <Label>Select Amenities</Label>
                        <p className="text-xs text-[#194a50]/60 mb-3">Choose all amenities available at this property</p>
                        
                        {/* Add new amenity */}
                        <div className="flex gap-2 mb-4">
                          <Input
                            value={newAmenity}
                            onChange={(e) => setNewAmenity(e.target.value)}
                            placeholder="Add new amenity..."
                            className="flex-1"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomAmenity())}
                            data-testid="new-amenity-input"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={addCustomAmenity}
                            className="border-[#194a50]/30"
                            data-testid="add-amenity-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {allAmenities.map((amenity) => (
                            <button
                              key={amenity}
                              type="button"
                              onClick={() => {
                                const has = formData.amenities.includes(amenity);
                                setFormData({
                                  ...formData,
                                  amenities: has 
                                    ? formData.amenities.filter(a => a !== amenity)
                                    : [...formData.amenities, amenity]
                                });
                              }}
                              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                                formData.amenities.includes(amenity)
                                  ? 'bg-[#194a50] text-white border-[#194a50]'
                                  : 'bg-white text-[#194a50] border-[#194a50]/30 hover:border-[#194a50]'
                              } ${customAmenities.includes(amenity) ? 'ring-2 ring-amber-400' : ''}`}
                            >
                              {amenity}
                              {customAmenities.includes(amenity) && <span className="ml-1 text-xs">(custom)</span>}
                            </button>
                          ))}
                        </div>
                        
                        <div className="mt-4">
                          <p className="text-sm text-[#194a50]/70">
                            Selected: {formData.amenities.length} amenities
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Pricing Tab */}
                    <TabsContent value="pricing" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Base Nightly Rate (€)</Label>
                          <Input
                            type="number"
                            value={formData.base_nightly_rate}
                            onChange={(e) => setFormData({ ...formData, base_nightly_rate: parseFloat(e.target.value) || 0 })}
                            data-testid="nightly-rate-input"
                          />
                        </div>
                        <div>
                          <Label>Min Stay (nights)</Label>
                          <Input
                            type="number"
                            value={formData.min_stay_nights}
                            onChange={(e) => setFormData({ ...formData, min_stay_nights: parseInt(e.target.value) || 1 })}
                            data-testid="min-stay-input"
                          />
                        </div>
                        <div>
                          <Label>Cleaning Fee (€)</Label>
                          <Input
                            type="number"
                            value={formData.cleaning_fee}
                            onChange={(e) => setFormData({ ...formData, cleaning_fee: parseFloat(e.target.value) || 0 })}
                            data-testid="cleaning-fee-input"
                          />
                        </div>
                        <div>
                          <Label>Check-in Fee (€)</Label>
                          <Input
                            type="number"
                            value={formData.check_in_fee}
                            onChange={(e) => setFormData({ ...formData, check_in_fee: parseFloat(e.target.value) || 0 })}
                            data-testid="checkin-fee-input"
                          />
                        </div>
                      </div>
                      
                      {/* Length-of-Stay Rate Brackets Section */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <Label>Length-of-Stay Rate Brackets</Label>
                            <p className="text-xs text-[#194a50]/60">Set multipliers or fixed rates based on stay duration. For a flat let by the month, choose Fixed Rate (€/month) — entering the nightly equivalent instead loses cents off every month.</p>
                          </div>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={addLengthOfStayRate}
                            className="border-[#194a50]/30"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Bracket
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          {formData.length_of_stay_rates.map((rate, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="grid grid-cols-12 gap-3 items-end">
                                <div className="col-span-2">
                                  <Label className="text-xs">Min Nights</Label>
                                  <Input
                                    type="number"
                                    value={rate.min_nights}
                                    onChange={(e) => updateLengthOfStayRate(index, 'min_nights', parseInt(e.target.value) || 0)}
                                    className="h-9"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Max Nights</Label>
                                  <Input
                                    type="number"
                                    value={rate.max_nights}
                                    onChange={(e) => updateLengthOfStayRate(index, 'max_nights', parseInt(e.target.value) || 0)}
                                    placeholder="999 = unlimited"
                                    className="h-9"
                                  />
                                </div>
                                <div className="col-span-3">
                                  <Label className="text-xs">Rate Type</Label>
                                  <Select 
                                    value={rate.rate_type} 
                                    onValueChange={(val) => updateLengthOfStayRate(index, 'rate_type', val)}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="multiplier">Multiplier</SelectItem>
                                      <SelectItem value="fixed">Fixed Rate (€/night)</SelectItem>
                                      <SelectItem value="monthly">Fixed Rate (€/month)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {rate.rate_type === 'multiplier' && (
                                  <div className="col-span-3">
                                    <Label className="text-xs">Multiplier (e.g., 0.9 = 10% off)</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={rate.multiplier}
                                      onChange={(e) => updateLengthOfStayRate(index, 'multiplier', parseFloat(e.target.value) || 1)}
                                      className="h-9"
                                    />
                                  </div>
                                )}
                                {rate.rate_type === 'fixed' && (
                                  <div className="col-span-3">
                                    <Label className="text-xs">Fixed Rate (€/night)</Label>
                                    <Input
                                      type="number"
                                      value={rate.fixed_rate}
                                      onChange={(e) => updateLengthOfStayRate(index, 'fixed_rate', parseFloat(e.target.value) || 0)}
                                      className="h-9"
                                    />
                                  </div>
                                )}
                                {rate.rate_type === 'monthly' && (
                                  <div className="col-span-3">
                                    <Label className="text-xs">Monthly Rent (€/month)</Label>
                                    <Input
                                      type="number"
                                      value={rate.monthly_rate ?? 0}
                                      onChange={(e) => updateLengthOfStayRate(index, 'monthly_rate', parseFloat(e.target.value) || 0)}
                                      className="h-9"
                                    />
                                    {/* Shown, not stored: the month is what bills. A 30-day
                                        cycle of a €700 flat bills €700 even though this reads
                                        €23.33 — entering 23.33 as a nightly rate bills €699.90. */}
                                    <p className="text-xs text-[#194a50]/60 mt-1">
                                      ≈ €{((parseFloat(rate.monthly_rate) || 0) / 30).toFixed(2)}/night · a 30-day month bills €{(parseFloat(rate.monthly_rate) || 0).toFixed(2)}
                                    </p>
                                  </div>
                                )}
                                <div className="col-span-2">
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => removeLengthOfStayRate(index)}
                                    className="h-9 w-full text-red-500 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {formData.length_of_stay_rates.length === 0 && (
                            <div className="text-center py-6 text-[#194a50]/50">
                              <p className="text-sm">No length-of-stay brackets configured</p>
                              <p className="text-xs">Base nightly rate will apply to all stays</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Seasonal Rates Section */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <Label>Seasonal / Block Rates</Label>
                            <p className="text-xs text-[#194a50]/60">Set different rates for specific periods</p>
                          </div>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={addSeasonalRate}
                            className="border-[#194a50]/30"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Season
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          {formData.seasonal_rates.map((rate, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="grid grid-cols-12 gap-3 items-end">
                                <div className="col-span-3">
                                  <Label className="text-xs">Season Name</Label>
                                  <Input
                                    value={rate.name}
                                    onChange={(e) => updateSeasonalRate(index, 'name', e.target.value)}
                                    placeholder="e.g., High Season"
                                    className="h-9"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Start Month</Label>
                                  <Select 
                                    value={rate.start_month?.toString()} 
                                    onValueChange={(val) => updateSeasonalRate(index, 'start_month', parseInt(val))}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {months.map((month, i) => (
                                        <SelectItem key={i} value={(i + 1).toString()}>{month}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">End Month</Label>
                                  <Select 
                                    value={rate.end_month?.toString()} 
                                    onValueChange={(val) => updateSeasonalRate(index, 'end_month', parseInt(val))}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {months.map((month, i) => (
                                        <SelectItem key={i} value={(i + 1).toString()}>{month}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Daily Rate (€)</Label>
                                  <Input
                                    type="number"
                                    value={rate.daily_rate}
                                    onChange={(e) => updateSeasonalRate(index, 'daily_rate', parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                    className="h-9"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Discount %</Label>
                                  <Input
                                    type="number"
                                    value={rate.discount_percentage}
                                    onChange={(e) => updateSeasonalRate(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                    className="h-9"
                                  />
                                </div>
                                <div className="col-span-1">
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => removeSeasonalRate(index)}
                                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {formData.seasonal_rates.length === 0 && (
                            <div className="text-center py-8 text-[#194a50]/50">
                              <Calendar className="w-8 h-8 mx-auto mb-2" />
                              <p className="text-sm">No seasonal rates configured</p>
                              <p className="text-xs">Base nightly rate will be used year-round</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    {/* ── Translations tab ────────────────────────────── */}
                    <TabsContent value="translations" className="space-y-4 mt-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-[#0c2431]">Per-language copy</h3>
                          <p className="text-xs text-[#194a50]/60 mt-0.5 max-w-lg">
                            English (on the <em>Basic Info</em> tab) is the source of truth. Empty fields below fall back to English on the public site.
                          </p>
                        </div>
                        <Button
                          type="button"
                          onClick={handleAutoTranslate}
                          disabled={autoTranslating || !selectedProperty}
                          className="bg-terracotta hover:bg-terracotta-dark text-white"
                          title={selectedProperty ? 'Auto-translate all empty languages via DeepL' : 'Save the property first, then run auto-translate'}
                        >
                          {autoTranslating
                            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Translating…</>
                            : <>🌐 Auto-translate with DeepL</>}
                        </Button>
                      </div>

                      {/* Language sub-tabs */}
                      <div className="flex items-center gap-1.5 flex-wrap border-b border-gray-100 pb-3">
                        {[
                          { code: 'es', flag: '🇪🇸', label: 'Español' },
                          { code: 'fi', flag: '🇫🇮', label: 'Suomi'   },
                          { code: 'sv', flag: '🇸🇪', label: 'Svenska' },
                          { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
                        ].map((l) => {
                          const isActive = translationLang === l.code;
                          const tr = formData.translations?.[l.code] || {};
                          const hasValue = !!(tr.title || tr.description || (Array.isArray(tr.amenities) && tr.amenities.length));
                          return (
                            <button
                              key={l.code}
                              type="button"
                              onClick={() => setTranslationLang(l.code)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                                isActive
                                  ? 'border-[#194a50] bg-[#194a50] text-white'
                                  : hasValue
                                    ? 'border-[#194a50]/30 bg-white text-[#194a50] hover:border-[#194a50]'
                                    : 'border-dashed border-gray-300 bg-white text-gray-400 hover:border-gray-400'
                              }`}
                              title={hasValue ? `${l.label} — has content` : `${l.label} — empty (falls back to English)`}
                            >
                              <span className="text-sm">{l.flag}</span>
                              {l.label}
                              {!hasValue && <span className="opacity-60 text-[10px]">empty</span>}
                            </button>
                          );
                        })}
                      </div>

                      {/* Title */}
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={formData.translations?.[translationLang]?.title || ''}
                          onChange={(e) => setTranslationField(translationLang, 'title', e.target.value)}
                          placeholder={`${formData.title || 'English title'} — leave blank to use English`}
                        />
                      </div>

                      {/* Description (rich HTML) */}
                      <div>
                        <Label>Description</Label>
                        <RichTextEditor
                          key={`desc-${translationLang}`}
                          value={formData.translations?.[translationLang]?.description || ''}
                          onChange={(html) => setTranslationField(translationLang, 'description', html)}
                          placeholder="Translated description — leave blank to use English"
                          minHeight={220}
                        />
                      </div>

                      {/* Amenities — translated as a parallel list */}
                      <div>
                        <Label>Amenities ({(formData.amenities || []).length} items)</Label>
                        <p className="text-[11px] text-[#194a50]/50 mt-0.5 mb-2">
                          One translation per line, in the same order as the English list. Leave a line blank to fall back to the English name for that amenity.
                        </p>
                        <Textarea
                          value={(formData.translations?.[translationLang]?.amenities || []).join('\n')}
                          onChange={(e) => setTranslationField(translationLang, 'amenities', e.target.value.split('\n'))}
                          rows={Math.max(5, (formData.amenities || []).length)}
                          placeholder={(formData.amenities || []).slice(0, 5).join('\n') || 'Pool\nWiFi\nAir conditioning\n…'}
                          className="font-mono text-xs"
                        />
                      </div>
                    </TabsContent>

                    <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                      <Button type="button" variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" data-testid="submit-property-btn">
                        {selectedProperty ? 'Update' : 'Create'} Property
                      </Button>
                    </div>
                  </form>
                </Tabs>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Properties Grid */}
      {properties.length === 0 ? (
        <Card className="shadow-sm border-[#194a50]/5">
          <CardContent className="py-16 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-[#194a50]/30" />
            <h3 className="text-xl font-semibold text-[#0c2431] mb-2">No properties found</h3>
            <p className="text-[#194a50]/60">
              {search ? 'Try a different search term' : 'Add your first property to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
          {properties.map((property) => (
            <Card 
              key={property.id} 
              className="overflow-hidden shadow-sm border-[#194a50]/5 hover:shadow-lg transition-all group"
              data-testid={`property-card-${property.id}`}
            >
              <div className="relative h-48 bg-[#e1dad2]">
                {property.photos?.[0] || property.photo_url ? (
                  <img
                    src={fixPhotoUrl(property.photos?.[0] || property.photo_url)}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-[#194a50]/30" />
                  </div>
                )}
                {property.photos?.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Image className="w-3 h-3" />
                    {property.photos.length}
                  </div>
                )}
                {(isAdmin || isCustomerService) && (
                  <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditDialog(property)}
                      className="p-2 bg-white rounded-lg shadow-md hover:bg-[#e1dad2] transition-colors"
                      data-testid={`edit-property-${property.id}`}
                      title="Edit property"
                    >
                      <Edit className="w-4 h-4 text-[#194a50]" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => setPropertyToDelete(property)}
                        className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                        data-testid={`delete-property-${property.id}`}
                        title="Delete property"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                )}
                {!property.is_published && (
                  <div className="absolute top-3 left-3">
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                      <EyeOff className="w-3 h-3" /> Unpublished
                    </span>
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-lg text-[#0c2431] line-clamp-1 font-['Outfit']">
                    {property.title}
                  </h3>
                  {property.reference && (
                    <span className="text-xs bg-[#194a50]/10 text-[#194a50] px-2 py-0.5 rounded font-mono">
                      {property.reference}
                    </span>
                  )}
                </div>
                {property.property_type && (
                  <span className="inline-block mb-1.5 text-[10px] font-semibold uppercase tracking-wide bg-[#f5f0e8] text-[#194a50] px-2 py-0.5 rounded-full capitalize">
                    {property.property_type}
                  </span>
                )}
                <div className="flex items-center gap-1 text-[#194a50]/70 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  {property.location?.city}, {property.location?.country}
                </div>
                
                {/* Bedrooms/Bathrooms/Guests */}
                <div className="flex items-center gap-4 text-[#194a50]/70 text-sm mb-3">
                  <span>{property.bedrooms || 1} bed{(property.bedrooms || 1) > 1 ? 's' : ''}</span>
                  <span>{property.bathrooms || 1} bath{(property.bathrooms || 1) > 1 ? 's' : ''}</span>
                  {property.max_guests && <span>{property.max_guests} guests</span>}
                </div>
                
                {/* Key Reference - only for admin/CS */}
                {canSeeKeyReference && property.key_reference && (
                  <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 flex items-center gap-1">
                    <Key className="w-3 h-3" />
                    <span className="font-medium">Key:</span> {property.key_reference}
                  </div>
                )}
                
                {/* Amenities preview */}
                {property.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {property.amenities.slice(0, 3).map((amenity) => (
                      <span key={amenity} className="text-xs bg-[#e1dad2] text-[#194a50] px-2 py-0.5 rounded">
                        {amenity}
                      </span>
                    ))}
                    {property.amenities.length > 3 && (
                      <span className="text-xs text-[#194a50]/60">+{property.amenities.length - 3}</span>
                    )}
                  </div>
                )}
                
                <p className="text-sm text-[#194a50]/60 line-clamp-2 mb-4">
                  {property.description}
                </p>
                
                {(isAdmin || isOwner) && property.operational_notes && (
                  <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                    <strong>Notes:</strong> {property.operational_notes}
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t border-[#194a50]/10">
                  <div className="flex items-center gap-1 text-[#194a50]">
                    <span className="font-semibold">€{property.base_nightly_rate}</span>
                    <span className="text-xs text-[#194a50]/60">/night</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {(isAdmin || isCustomerService) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#194a50]/60 hover:bg-[#194a50]/10 hover:text-[#194a50]"
                        onClick={() => setSelectedProperty(property)}
                        title="Edit property"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#194a50] hover:bg-[#194a50]/10"
                      onClick={() => window.open(`/preview/${property.id}`, '_blank')}
                      title="Preview live listing"
                    >
                      View <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!propertyToDelete} onOpenChange={(open) => { if (!open) setPropertyToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{propertyToDelete?.title}"</strong>?
              This will permanently remove the property and all its photos. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProperty}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Property
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PropertiesPage;
